import { useCallback, useRef, useState } from 'react';
import { FileText, Paperclip, Upload } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@roger/ui-components';
import {
  client,
  getTaskDocuments,
  getTaskDocumentUrl,
  uploadTaskDocument,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';

interface TaskDocumentsPopoverProps {
  taskId: string;
  initialCount?: number;
}

interface TaskDocument {
  id: string;
  file_name: string;
}

export default function TaskDocumentsPopover({
  taskId,
  initialCount,
}: TaskDocumentsPopoverProps) {
  const [documents, setDocuments] = useState<TaskDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const hasFetchedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { error: showError } = useToast();

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getTaskDocuments({
        client,
        path: { task_id: taskId },
      });
      if (data) {
        setDocuments(data as TaskDocument[]);
        hasFetchedRef.current = true;
      }
    } catch (err) {
      console.error('Failed to load task documents', err);
      showError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [taskId, showError]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      fetchDocuments();
    }
  };

  const handleFileClick = async (docId: string) => {
    try {
      const { data } = await getTaskDocumentUrl({
        client,
        path: { task_id: taskId, document_id: docId },
      });
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get document URL', err);
      showError('Failed to open document. Please try again.');
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await uploadTaskDocument({
        client,
        path: { task_id: taskId },
        body: { file },
      });
      await fetchDocuments();
    } catch (err) {
      console.error('Failed to upload document', err);
      showError('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const badgeCount = hasFetchedRef.current
    ? documents.length
    : (initialCount ?? 0);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className='relative rounded p-1 hover:bg-muted'
          onClick={e => e.stopPropagation()}
        >
          <Paperclip className='h-4 w-4 text-muted-foreground' />
          {badgeCount > 0 ? (
            <span className='absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-blue-500 text-[9px] font-medium text-white'>
              {badgeCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-0' onClick={e => e.stopPropagation()}>
        <div className='border-b px-3 py-2'>
          <p className='text-xs font-medium text-muted-foreground'>Documents</p>
        </div>
        <div className='max-h-48 overflow-y-auto'>
          {loading ? (
            <p className='px-3 py-2 text-xs text-muted-foreground'>
              Loading...
            </p>
          ) : documents.length === 0 ? (
            <p className='px-3 py-2 text-xs text-muted-foreground'>
              No documents attached
            </p>
          ) : (
            documents.map(doc => (
              <button
                key={doc.id}
                className='flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-muted/50'
                onClick={() => handleFileClick(doc.id)}
              >
                <FileText className='h-3.5 w-3.5 shrink-0 text-blue-500' />
                <span className='truncate'>{doc.file_name}</span>
              </button>
            ))
          )}
        </div>
        <div className='border-t px-3 py-2'>
          <input
            ref={fileInputRef}
            type='file'
            className='hidden'
            onChange={handleUpload}
            accept='.pdf,.png,.jpg,.jpeg,.doc,.docx'
          />
          <button
            className='flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50'
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <Upload className='h-3 w-3' />
            {uploading ? 'Uploading...' : 'Upload document'}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
